/**
 * Deterministic fixed-step state for a worker walking an authored feeding round.
 *
 * This deliberately contains no scene, physics, or clock dependencies. The
 * renderer can read `state`; anything that needs to react to the worker sees the
 * same values the fixed simulation produced.
 */

/** A stop on the round. */
export interface Waypoint {
  x: number;
  z: number;
  /** Seconds he stands here once he arrives. May be 0. */
  dwell: number;
  /**
   * Radians he turns to face while dwelling. Omit to keep his travel facing.
   * Same convention as Watcher.facing in stealth.ts: 0 faces +Z, increasing toward +X.
   */
  facing?: number;
}

export interface WorkerRoute {
  /** Where he stands when off-stage: before the first round and between rounds. */
  park: { x: number; z: number };
  /** Metres per second while walking. */
  speed: number;
  /** Radians per second he turns, shortest way round. */
  turnRate: number;
  /** Metres between footfalls. */
  stride: number;
  /** The round, in order. Must not be empty. */
  waypoints: readonly Waypoint[];
  /** Seconds parked between one round finishing and the next beginning. */
  restBetweenRounds: number;
  /**
   * A point he passes on every way back to `park`, the end of a round and an
   * `interrupt()` alike. Without it he goes straight back from wherever he
   * is, which is right in an open room and wrong when the park is through a
   * doorway: beat 2's line home from the near bay crossed the walkway door
   * 0.29 m from a jamb, inside his 0.32 m half-width (`walkway-door.spec.ts`).
   */
  returnVia?: { x: number; z: number };
}

export type WorkerPhase = 'parked' | 'walking' | 'dwelling' | 'returning';

export interface WorkerState {
  x: number;
  z: number;
  /** Radians, 0 faces +Z, increasing toward +X. */
  facing: number;
  phase: WorkerPhase;
  /** Which waypoint he is walking to, or standing at. 0-based. */
  waypoint: number;
  /** Seconds left of the current dwell; 0 when not dwelling. */
  dwellRemaining: number;
  /** How many complete rounds have finished since start(). */
  roundsCompleted: number;
  /** 0..1 through the current round, for authored staging to read. */
  roundProgress: number;
  /** True on exactly the step a foot lands, false otherwise. */
  footfall: boolean;
}

const EPSILON = 0.000000001;

function finite(value: number, label: string): void {
  if (!Number.isFinite(value)) throw new Error(`worker route ${label} must be finite`);
}

function positive(value: number, label: string): void {
  finite(value, label);
  if (value <= 0) throw new Error(`worker route ${label} must be positive`);
}

function nonNegative(value: number, label: string): void {
  finite(value, label);
  if (value < 0) throw new Error(`worker route ${label} must not be negative`);
}

/** Normalise to (-PI, PI], avoiding a second angle convention in presentation. */
function normaliseAngle(angle: number): number {
  let normalised = (angle + Math.PI) % (Math.PI * 2);
  if (normalised <= 0) normalised += Math.PI * 2;
  return normalised - Math.PI;
}

function bearing(fromX: number, fromZ: number, toX: number, toZ: number): number {
  return Math.atan2(toX - fromX, toZ - fromZ);
}

export class Worker {
  readonly #route: WorkerRoute;
  /** Kept as scalars so this is the only mutable state object allocated by the simulation. */
  readonly #state: WorkerState;
  #started = false;
  #restRemaining = 0;
  #distanceSinceFootfall = 0;
  /** Returning, and `returnVia` not yet reached on this return. */
  #viaPending = false;

  constructor(route: WorkerRoute) {
    if (route.waypoints.length === 0) throw new Error('worker route must contain at least one waypoint');
    finite(route.park.x, 'park.x');
    finite(route.park.z, 'park.z');
    if (route.returnVia !== undefined) {
      finite(route.returnVia.x, 'returnVia.x');
      finite(route.returnVia.z, 'returnVia.z');
    }
    positive(route.speed, 'speed');
    positive(route.turnRate, 'turnRate');
    positive(route.stride, 'stride');
    nonNegative(route.restBetweenRounds, 'restBetweenRounds');
    for (const [index, waypoint] of route.waypoints.entries()) {
      finite(waypoint.x, `waypoint ${String(index)}.x`);
      finite(waypoint.z, `waypoint ${String(index)}.z`);
      nonNegative(waypoint.dwell, `waypoint ${String(index)}.dwell`);
      if (waypoint.facing !== undefined) finite(waypoint.facing, `waypoint ${String(index)}.facing`);
    }

    this.#route = route;
    const first = route.waypoints[0];
    if (first === undefined) throw new Error('worker route must contain at least one waypoint');
    this.#state = {
      x: route.park.x,
      z: route.park.z,
      facing: normaliseAngle(bearing(route.park.x, route.park.z, first.x, first.z)),
      phase: 'parked',
      waypoint: 0,
      dwellRemaining: 0,
      roundsCompleted: 0,
      roundProgress: 0,
      footfall: false,
    };
  }

  /** A read-only view of current state. Callers must not mutate this object. Same identity every call. */
  get state(): Readonly<WorkerState> {
    return this.#state;
  }

  /** Begin the first round. Calling this during a round deliberately does not reset it. */
  start(): void {
    if (this.#started) return;
    this.#started = true;
    this.#beginRound();
  }

  /**
   * Put a parked worker at `park` and start a fresh round immediately.
   *
   * **Only legal while he is parked**, and the guard is not defensive politeness:
   * this snaps his position, so calling it on a man who is walking down a lit
   * corridor teleports him in front of the player. Parked is the one phase where
   * that cannot be seen, because a park is authored off-stage.
   *
   * Beat 4 needs it because `start()` deliberately does nothing once a worker has
   * begun — `interrupt()` followed by `start()` reads like "put him back to the
   * top of his round" and actually means "send him out of the room and leave him
   * there for `restBetweenRounds`". Stage 5 was staged that way and played as the
   * handler *leaving*, with the dog following him out through the door and the
   * beat's one important frame happening off-screen behind a closed door.
   */
  restart(): void {
    if (this.#state.phase !== 'parked') return;
    this.#state.x = this.#route.park.x;
    this.#state.z = this.#route.park.z;
    this.#restRemaining = 0;
    this.#started = true;
    this.#beginRound();
  }

  /**
   * End the current dwell now and send him back to `park`.
   *
   * The beat needs this: when Rosalie is spotted, the worker's forty seconds at
   * the far trough stop being forty seconds — he straightens up and leaves, and
   * the window she was crossing closes under her. Without it the only way to
   * express that would be to rebuild the route mid-round, which would restart
   * his position and make the reaction read as a teleport.
   */
  interrupt(): void {
    if (this.#state.phase !== 'walking' && this.#state.phase !== 'dwelling') return;
    this.#state.dwellRemaining = 0;
    this.#state.roundProgress = 1;
    this.#beginReturn();
  }

  #beginReturn(): void {
    this.#state.phase = 'returning';
    this.#viaPending = this.#route.returnVia !== undefined;
  }

  /** Advance exactly one fixed simulation step. Returns the new state. */
  step(dt: number): Readonly<WorkerState> {
    if (!Number.isFinite(dt) || dt < 0) throw new Error('worker step dt must be finite and not negative');
    this.#state.footfall = false;
    if (!this.#started || dt === 0) return this.#state;

    let remaining = dt;
    // A route can legally contain coincident points and zero waits. Bound the
    // hand-off loop so malformed authored content cannot turn one simulation tick
    // into an unbounded busy loop; stopping is safer than inventing elapsed time.
    const transitions = this.#route.waypoints.length * 2 + 4;
    for (let count = 0; remaining > EPSILON && count < transitions; count += 1) {
      if (this.#state.phase === 'walking' || this.#state.phase === 'returning') {
        remaining = this.#walk(remaining);
      } else if (this.#state.phase === 'dwelling') {
        remaining = this.#dwell(remaining);
      } else {
        remaining = this.#rest(remaining);
      }
    }
    return this.#state;
  }

  #beginRound(): void {
    this.#state.phase = 'walking';
    this.#state.waypoint = 0;
    this.#state.dwellRemaining = 0;
    this.#state.roundProgress = 0;
  }

  #walk(remaining: number): number {
    const returning = this.#state.phase === 'returning';
    const via = returning && this.#viaPending ? this.#route.returnVia : undefined;
    const target = via ?? (returning ? this.#route.park : this.#waypoint());
    const dx = target.x - this.#state.x;
    const dz = target.z - this.#state.z;
    const distance = Math.hypot(dx, dz);
    if (distance <= EPSILON) {
      if (via !== undefined) this.#viaPending = false;
      else this.#arrive(returning);
      return remaining;
    }

    const elapsed = Math.min(remaining, distance / this.#route.speed);
    const travelled = elapsed * this.#route.speed;
    const direction = bearing(this.#state.x, this.#state.z, target.x, target.z);
    this.#turnToward(direction, elapsed);
    this.#state.x += (dx / distance) * travelled;
    this.#state.z += (dz / distance) * travelled;
    this.#recordDistance(travelled);

    if (elapsed + EPSILON < distance / this.#route.speed) return 0;
    this.#state.x = target.x;
    this.#state.z = target.z;
    // Passing the via point is not an arrival: no dwell, no park, walk on.
    if (via !== undefined) this.#viaPending = false;
    else this.#arrive(returning);
    // Every phase hands unused tick time back to `step`. Dropping it at an
    // arrival, dwell, or rest boundary makes a round's duration depend on the
    // fixed-step size instead of the authored route.
    return remaining - elapsed;
  }

  #arrive(returning: boolean): void {
    this.#state.dwellRemaining = 0;
    if (returning) {
      this.#state.phase = 'parked';
      this.#state.roundsCompleted += 1;
      this.#state.roundProgress = 0;
      this.#restRemaining = this.#route.restBetweenRounds;
      return;
    }

    this.#state.phase = 'dwelling';
    this.#state.dwellRemaining = this.#waypoint().dwell;
  }

  #dwell(remaining: number): number {
    const waypoint = this.#waypoint();
    if (waypoint.facing !== undefined) this.#turnToward(waypoint.facing, Math.min(remaining, this.#state.dwellRemaining));
    const elapsed = Math.min(remaining, this.#state.dwellRemaining);
    this.#state.dwellRemaining -= elapsed;
    if (this.#state.dwellRemaining > EPSILON) return 0;

    this.#state.dwellRemaining = 0;
    if (this.#state.waypoint + 1 < this.#route.waypoints.length) {
      this.#state.waypoint += 1;
      this.#state.phase = 'walking';
      this.#state.roundProgress = this.#state.waypoint / this.#route.waypoints.length;
    } else {
      this.#beginReturn();
      this.#state.roundProgress = 1;
    }
    return remaining - elapsed;
  }

  #rest(remaining: number): number {
    if (this.#restRemaining <= EPSILON) {
      this.#restRemaining = 0;
      this.#beginRound();
      return remaining;
    }
    const elapsed = Math.min(remaining, this.#restRemaining);
    this.#restRemaining -= elapsed;
    if (this.#restRemaining > EPSILON) return 0;
    this.#restRemaining = 0;
    this.#beginRound();
    return remaining - elapsed;
  }

  #waypoint(): Waypoint {
    const waypoint = this.#route.waypoints[this.#state.waypoint];
    if (waypoint === undefined) throw new Error('worker waypoint index escaped route');
    return waypoint;
  }

  #turnToward(target: number, dt: number): void {
    const delta = normaliseAngle(target - this.#state.facing);
    const step = Math.min(Math.abs(delta), this.#route.turnRate * dt);
    this.#state.facing = normaliseAngle(this.#state.facing + Math.sign(delta) * step);
  }

  #recordDistance(distance: number): void {
    this.#distanceSinceFootfall += distance;
    // A long fixed step may cross several stride marks. Expose only one event
    // this step, but retain the other marks so later steps still emit them.
    if (!this.#state.footfall && this.#distanceSinceFootfall + EPSILON >= this.#route.stride) {
      this.#distanceSinceFootfall -= this.#route.stride;
      this.#state.footfall = true;
    }
  }
}
